import { CheckIcon, MinusCircleFillIcon } from "@eworkit/iris";
import { UnreadNotificationsStatus } from "@eworkit/messenger";
import React, { FC, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";

import { testAttr } from "@/browser";
import { Badge } from "@/components/Badge";
import { Icon } from "@/components/Icon";
import { Text } from "@/components/Text";
import { UserContext } from "@/components/User";
import { ICompany } from "@/data/companies";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { formatFullName } from "@/data/persons";
import { Flex } from "@/eworkitComponents";

import { LazyUserAvatar } from "../../../../../../components-new";

import "./_context-dropdown-profile.scss";

interface IContextDropdownProfileProps {
  profile: IConsultantProfile;
  onClick?: (profile: IConsultantProfile) => void;
  showNotificationsIndicator?: boolean;
  showActiveIndicator?: boolean;
  showCompany?: boolean;
}

export const ContextDropdownProfile: FC<IContextDropdownProfileProps> = ({
  profile,
  onClick,
  showNotificationsIndicator = true,
  showActiveIndicator = true,
  showCompany = true,
}) => {
  const User = useContext(UserContext);
  const { t } = useTranslation("session");

  const profileCompany =
    (profile.sharedProfile && profile.sharedProfile.company) ||
    (profile.requestToJoin && profile.requestToJoin.company);

  const isActive =
    showActiveIndicator &&
    (User.contextCompany && profileCompany
      ? User.contextCompany.id === profileCompany.id
      : User.contextConsultantProfileId === profile.id);

  const profileName = formatFullName(profile);

  const profileGuid = profile.sharedProfile ? profile.sharedProfile.company.guid : null;

  const profileRole = profileCompany
    ? t(`COMPONENTS.TOPBAR.CONTEXT_DROPDOWN.${profile.companyRole}`)
    : t("COMPONENTS.TOPBAR.CONTEXT_DROPDOWN.PRIVATE_PROFILE");

  const hasPendingTasks =
    showNotificationsIndicator &&
    profile.status !== "PENDING" &&
    profile.notDisplayedTaskCount !== null &&
    profile.notDisplayedTaskCount > 0;

  const testAttributes = profileCompany
    ? testAttr({ testName: `company[${profileCompany.id}]-profile[${profile.id}]` }, { sufix: "context-switch" })
    : testAttr({ testName: `profile[${profile.id}]` }, { sufix: "context-switch" });

  const handleClick = useCallback(() => (onClick ? onClick(profile) : null), [onClick, profile]);

  const baseClassName = "context-dropdown-profile";

  return (
    <div
      key={profile.id}
      onClick={onClick ? handleClick : undefined}
      className={`${baseClassName}__wrapper`}
      {...testAttributes}>
      <Flex
        className={onClick ? baseClassName : undefined}
        padding={[false, "sm", "md", "xxs"]}
        innerMargin="sm"
        align="flex-start">
        <LazyUserAvatar
          avatar={profile.avatar}
          firstName={profile.firstName}
          lastName={profile.lastName}
          company={showCompany ? (profileCompany as ICompany) : null}
        />
        <Flex direction="row" justify="space-between" innerMargin="xxl" className={`${baseClassName}--name-container`}>
          <Flex direction="column">
            <Text size="lg" color="black" weight={isActive ? "bold" : "light"} className="-word-break-word" hjSuppress>
              {profileName}
            </Text>
            {!profileCompany || !showCompany ? (
              <Text size="md" color="grey1">
                {profileRole}
              </Text>
            ) : (
              <Text size="md" color="grey1">
                {t("COMPONENTS.TOPBAR.CONTEXT_DROPDOWN.ROLE_AT_COMPANY", {
                  role: profileRole,
                  company: profileCompany?.name,
                })}
              </Text>
            )}
          </Flex>

          <Flex align="center" justify="flex-end" grow={1}>
            {profile.blocked ? (
              <MinusCircleFillIcon size="md" color="r500" />
            ) : (
              <>
                {isActive && <CheckIcon size="md" color="vg500" />}

                {profile.status === "PENDING" && (
                  <Badge backgroundColor="default" borderColor="default">
                    <Flex padding={[false, "sm"]}>
                      <Text uppercase size="md" color="background-card" weight="bold">
                        {t("COMPONENTS.TOPBAR.CONTEXT_DROPDOWN.BADGE_NEW")}
                      </Text>
                    </Flex>
                  </Badge>
                )}

                <UnreadNotificationsStatus>
                  {(_, unreadNotificationsProfiles) =>
                    unreadNotificationsProfiles.includes(profileGuid) || hasPendingTasks ? (
                      <Flex padding={[false, "sm"]}>{<Icon icon="circle" color="link" size="sm" />}</Flex>
                    ) : null
                  }
                </UnreadNotificationsStatus>
              </>
            )}
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};
